import { useMutation, useQuery } from "@apollo/client/react";
import mixpanel from "mixpanel-browser";
import { useMemo } from "react";

import { alert } from "../components/common/Alert";
import { DISMISS_NOTIFICATIONS_BY_NOTE } from "../graphql/mutations/DismissNotificationsByNote";
import { RETRY_FAILED_AUTO_SOAP } from "../graphql/mutations/RetryFailedAutoSoap";
import { USER_NOTIFICATIONS } from "../graphql/queries/UserNotifications";

const NOTIFICATION_TYPE = "new_audio_available";

interface Note {
  uuid: string;
  jobType?: string;
  autoTemplates?: { uuid: string };
}

export function useNewAudioNotification(
  note: Note | null,
  refetchNote?: () => Promise<unknown>,
) {
  const { data, loading, refetch } = useQuery(USER_NOTIFICATIONS, {
    variables: {
      notificationType: NOTIFICATION_TYPE,
      notificationDomain: "scribe",
    },
    skip: !note?.uuid,
    fetchPolicy: "cache-and-network",
  });

  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);
  const [dismissNotificationsByNote] = useMutation(
    DISMISS_NOTIFICATIONS_BY_NOTE,
  );

  const noteUuidsWithNewAudio = useMemo(
    () =>
      new Set(
        data?.userNotifications
          ?.map((n) => n?.noteUuid)
          .filter((uuid): uuid is string => uuid != null) ?? [],
      ),
    [data?.userNotifications],
  );

  const hasNewAudio = noteUuidsWithNewAudio.has(note?.uuid ?? "");

  const dismiss = () =>
    dismissNotificationsByNote({
      variables: {
        noteUuid: note!.uuid,
        notificationType: NOTIFICATION_TYPE,
      },
    });

  const handleDismiss = async () => {
    if (!note?.uuid) return;
    try {
      await dismiss();
      await refetch();
    } catch (e) {
      alert(
        "error",
        e instanceof Error
          ? e.message
          : "Failed to dismiss notification",
      );
    }
  };

  const handleReprocessWithNewAudio = async () => {
    if (!note?.uuid) return;
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: note.uuid,
          jobType: note.jobType,
          templateUuid: note.autoTemplates?.uuid,
        },
      });
    } catch (e) {
      alert(
        "error",
        e instanceof Error ? e.message : "Failed to reprocess note",
      );
      mixpanel.track("Failed to reprocess note with new audio", {
        noteUuid: note.uuid,
        error: e instanceof Error ? e.message : "Unknown",
      });
      return;
    }

    alert("warning", "Reprocessing note with updated audio...");
    mixpanel.track("Note reprocessed with new audio", {
      noteUuid: note.uuid,
      jobType: note.jobType,
      source: "new_audio_notification_banner",
    });

    // Cleanup — failures here are non-critical since the reprocess already succeeded
    await Promise.all([
      dismiss().catch(() => {}),
      refetch().catch(() => {}),
      refetchNote?.().catch(() => {}),
    ]);
  };

  return {
    hasNewAudio,
    noteUuidsWithNewAudio,
    loading,
    handleReprocessWithNewAudio,
    handleDismiss,
  };
}
